// src/requestPermission.ts
import apiClient from './axios';
import { FIREBASE_VAPID_KEY } from '../env';
import { messaging } from './firebase';
import { getToken } from 'firebase/messaging';
import { getLoggerRef } from '../integrations/logger/logger-bridge';

const FCM_SW_URL = '/firebase-messaging-sw.js';

// Keyed by user so a logout/login in the same tab (which never reloads the
// page, so this module state survives) re-registers the device against the
// new account instead of leaving it bound to the previous one.
let registeredUserId: string | null = null;
let registeredToken: string | null = null;
let inFlight: { userId: string | null; promise: Promise<void> } | null = null;
// Bumped on logout. An async registration started before the bump belongs to a
// session that no longer exists, so it must not write the cache or toast.
let registrationGeneration = 0;

function generateDeviceId() {
  const existingId = localStorage.getItem('deviceId');
  if (existingId) return existingId;

  const newId = crypto.randomUUID();
  localStorage.setItem('deviceId', newId);
  return newId;
}

/** `register()` can resolve while the worker is still installing. Firebase getToken requires an active SW. */
export function waitForActiveServiceWorker(
  registration: ServiceWorkerRegistration
): Promise<ServiceWorkerRegistration> {
  if (registration.active) {
    return Promise.resolve(registration);
  }

  const worker = registration.installing || registration.waiting;
  if (!worker) {
    return Promise.reject(
      new Error('Service worker registration has no worker to activate')
    );
  }

  return new Promise((resolve, reject) => {
    const onStateChange = () => {
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', onStateChange);
        resolve(registration);
      } else if (worker.state === 'redundant') {
        worker.removeEventListener('statechange', onStateChange);
        reject(new Error('Service worker became redundant before activation'));
      }
    };

    worker.addEventListener('statechange', onStateChange);
    onStateChange();
  });
}

export const requestNotificationPermission = (
  userId?: string | null
): Promise<void> => {
  const identity = userId ?? null;

  if (registeredToken && registeredUserId === identity) {
    return Promise.resolve();
  }

  if (inFlight && inFlight.userId === identity) {
    return inFlight.promise;
  }

  const generation = registrationGeneration;
  const promise = registerForPushNotifications(identity, generation).finally(() => {
    if (inFlight?.promise === promise) {
      inFlight = null;
    }
  });
  inFlight = { userId: identity, promise };

  return promise;
};

/** Drop the cached registration so the next call re-binds the device. */
export function resetPushRegistration(): void {
  registeredUserId = null;
  registeredToken = null;
  inFlight = null;
  registrationGeneration += 1;
}

async function registerForPushNotifications(
  userId: string | null,
  generation: number
): Promise<void> {
  const isCurrentSession = () => generation === registrationGeneration;

  if (!FIREBASE_VAPID_KEY) {
    console.warn(
      "Web push registration skipped: REACT_APP_FIREBASE_VAPID_KEY is not set."
    );
    return;
  }

  try {
    localStorage.removeItem('fcmToken');

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return;
    }

    const registration = await navigator.serviceWorker.register(FCM_SW_URL, { scope: '/' });
    await waitForActiveServiceWorker(registration);
    const currentToken = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!currentToken) {
      if (isCurrentSession()) {
        // Background, non-essential feature: nothing the user can act on, so log only.
        getLoggerRef()?.warn("Web push registration: no FCM token available");
      }
      return;
    }

    if (!isCurrentSession()) {
      return;
    }

    await apiClient.post("/notifications/devices", {
      firebaseToken: currentToken,
      deviceID: generateDeviceId(),
    });

    if (!isCurrentSession()) {
      return;
    }
    registeredToken = currentToken;
    registeredUserId = userId;
  } catch (error) {
    if (!isCurrentSession()) {
      return;
    }
    registeredToken = null;
    registeredUserId = null;
    // Background, non-essential feature: nothing the user can act on, so log only.
    getLoggerRef()?.warn("Web push registration failed", {
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  }
}
